import { STAYS_WISHLIST } from "@capone/experiments";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import {
  HotelAvailabilityMapTooltip,
  Icon,
  IconName,
  ensureExhaustive,
} from "halifax";
import React from "react";
import {
  ListingCollectionEnum,
  ListingSearchResult,
  Lodging,
  LodgingCollectionEnum,
} from "redmond";
import { StayType } from "redmond/hotels-module/interfaces";
import { config } from "../../../../../../api/config";
import { ClientContext } from "../../../../../../App";
import {
  AVAILABLE,
  LODGING_PROMOTIONS,
  LODGING_PROMOTIONS_AVAILABLE,
  LODGING_PROMOTIONS_VARIANTS,
  LUXURY_PAY_AT_HOTEL_BADGE,
  ALLOWED_PAY_LATER_LODGINGS,
  AllowedPayLaterLodgingsConfig,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  getExperimentVariantJSON,
  useExperiments,
} from "../../../../../../context/experiments";
import { isAllowedPayLaterLodging } from "../../../../../shop/utils/payLater";
import { WishlistToggleHeart } from "../../../../../wishlist";
import { ConnectedAvailabilityMapHotelTooltipProps } from "./container";
import "./styles.scss";

export interface IAvailabilityMapHotelTooltipProps
  extends ConnectedAvailabilityMapHotelTooltipProps {
  lodging: Lodging | ListingSearchResult;
  className?: string;
  onClickHotelDetails: (event: React.MouseEvent) => void;
  onCloseClick: (event: React.MouseEvent) => void;
}

enum CollectionEnum {
  Premier = "Premier",
  Lifestyle = "Lifestyle",
}

const getCollection = (
  collection?: LodgingCollectionEnum | ListingCollectionEnum
): CollectionEnum | undefined => {
  switch (collection) {
    case LodgingCollectionEnum.Premier:
    case ListingCollectionEnum.Premier:
      return CollectionEnum.Premier;
    case LodgingCollectionEnum.Lifestyle:
    case ListingCollectionEnum.Lifestyle:
      return CollectionEnum.Lifestyle;
    case LodgingCollectionEnum.NoCollection:
    case ListingCollectionEnum.NoCollection:
    case undefined:
      return undefined;
    default:
      return ensureExhaustive(collection);
  }
};

const getIcon = (collection?: CollectionEnum): JSX.Element | undefined => {
  if (collection == null) return undefined;
  switch (collection) {
    case CollectionEnum.Premier:
      return <Icon name={IconName.PremierCollectionRibbon} />;
    case CollectionEnum.Lifestyle:
      return <Icon name={IconName.LifestyleCollectionRibbon} />;
    default:
      return ensureExhaustive(collection);
  }
};

export const AvailabilityMapHotelTooltip = (
  props: IAvailabilityMapHotelTooltipProps
): JSX.Element => {
  const {
    lodging,
    className,
    nightCount,
    accountReferenceId,
    isVentureX,
    onClickHotelDetails,
    onCloseClick,
  } = props;

  const expState = useExperiments();

  const lodgingPromotions = getExperimentVariantCustomVariants(
    expState.experiments,
    LODGING_PROMOTIONS,
    LODGING_PROMOTIONS_VARIANTS
  );
  const isLodgingPromotionsExperiment = React.useMemo(
    () => lodgingPromotions === LODGING_PROMOTIONS_AVAILABLE,
    [lodgingPromotions]
  );

  const isPayAtHotelBadgeExperimentEnabled =
    getExperimentVariant(expState.experiments, LUXURY_PAY_AT_HOTEL_BADGE) ===
    AVAILABLE;

  const allowedPayLaterLodgingIds = React.useMemo(() => {
    const config = getExperimentVariantJSON(
      expState.experiments,
      ALLOWED_PAY_LATER_LODGINGS
    ) as AllowedPayLaterLodgingsConfig | null;
    return config?.data?.lodgingIds;
  }, [expState.experiments]);

  const lodgingId = "lodging" in lodging ? lodging.lodging.id : lodging.listingId.id;
  const isPayAtHotelBadgeEnabled =
    isPayAtHotelBadgeExperimentEnabled &&
    isAllowedPayLaterLodging(allowedPayLaterLodgingIds, lodgingId);

  const wishlistExperiment = getExperimentVariant(
    expState.experiments,
    STAYS_WISHLIST
  );
  const isWishlistExperiment = React.useMemo(
    () => wishlistExperiment === AVAILABLE,
    [wishlistExperiment]
  );

  const collection = getCollection(
    "lodging" in lodging
      ? lodging.lodgingCollection
      : lodging.listing.listingCollection
  );
  const { policies } = React.useContext(ClientContext);

  const commonStayType = React.useMemo(() => {
    if ("lodging" in lodging) {
      return StayType.Hotels;
    }
    return StayType.Homes;
  }, [lodging]);

  return (
    <Box
      className={clsx(
        "lodging-details-container",
        {
          "premier-collection": collection === CollectionEnum.Premier,
          "lifestyle-collection": collection === CollectionEnum.Lifestyle,
          "wishlist-enabled": isWishlistExperiment,
        },
        className
      )}
    >
      {getIcon(collection)}
      <HotelAvailabilityMapTooltip
        lodging={lodging}
        onClickHotelDetails={onClickHotelDetails}
        onCloseClick={onCloseClick}
        policyLimit={policies?.hotels.policies[0].maxPricePerNight}
        tenant={config.TENANT}
        nightCount={nightCount ?? 0}
        rewardsKey={accountReferenceId}
        isLodgingPromotionsExperiment={isLodgingPromotionsExperiment}
        isVentureX={isVentureX}
        isPayAtHotelBadgeEnabled={isPayAtHotelBadgeEnabled}
        wishlistComponent={
          isWishlistExperiment ? (
            <WishlistToggleHeart
              listingId={
                "lodging" in lodging ? lodging.lodging.id : lodging.listingId.id
              }
              listingImageUrl={
                "lodging" in lodging
                  ? lodging.lodging.media?.[0]?.url
                  : lodging.listing.content.media?.[0]?.url
              }
              stayType={commonStayType}
              collection={
                "lodging" in lodging
                  ? lodging.lodgingCollection
                  : lodging.listing.listingCollection
              }
            />
          ) : undefined
        }
      />
    </Box>
  );
};
